import React from "react";

import {
  ImageData,
  ButtonData,
  backImageData,
  frontImageData,
  backButtonData,
  frontButtonData,
} from "./selectAreasOfInterestConst";
import "./SelectAreasOfInterest.css";
import { AreaOfInterestType } from "api/type";
import NormalText from "../NormalText";
import { ThemeContext } from "contexts";
import { isAISupportedArea } from "utils/isAISuportedProcedureAndArea";

const FrontImage =
  "https://clinic-os.com/flow-assets/select-area-of-interest/front.png";
const BackImage =
  "https://clinic-os.com/flow-assets/select-area-of-interest/back.png";

type SelectAreasOfInterestProps = {
  initialSelectedAreasOfInterest?: AreaOfInterestType[];
  handleSaveSelectedAreaOfInterest: (
    selectedBodyParts: AreaOfInterestType[],
  ) => void;
};

const SelectAreasOfInterest: React.FC<SelectAreasOfInterestProps> = ({
  initialSelectedAreasOfInterest = [],
  handleSaveSelectedAreaOfInterest,
}) => {
  const theme = React.useContext(ThemeContext);

  const [selectedBodyParts, setSelectedBodyParts] = React.useState<
    Set<AreaOfInterestType>
  >(new Set(initialSelectedAreasOfInterest));

  const handleButtonClick = (value: AreaOfInterestType) => {
    setSelectedBodyParts((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(value)) {
        newSelected.delete(value);
      } else {
        newSelected.add(value);
      }
      return newSelected;
    });
  };

  React.useEffect(() => {
    handleSaveSelectedAreaOfInterest(Array.from(selectedBodyParts));
    // NOTE: This is not a good way to update the state from
    // the parent component.
    // TODO: Find a better way to update the state from the parent component.
    // eslint-disable-next-line
  }, [selectedBodyParts]);

  const renderImagesAndButtons = (
    imageData: ImageData[],
    buttonData: ButtonData[],
    imageClassName: string = "areas-of-interest__front-image",
  ) => (
    <>
      {imageData.map((data, index) => (
        <img
          key={index}
          className={imageClassName}
          src={data.image}
          alt={data.areaOfInterest}
          draggable={false}
          style={{
            display: selectedBodyParts.has(data.areaOfInterest)
              ? "block"
              : "none",
          }}
        />
      ))}
      {buttonData.map((buttonData, index) => (
        <button
          key={index}
          className="areas-of-interest__button"
          style={{
            top: buttonData.top,
            left: buttonData.left,
            width: buttonData.width,
            height: buttonData.height,
          }}
          onClick={(e) => {
            e.preventDefault();
            handleButtonClick(buttonData.areaOfInterest);
          }}
        >
          <input
            key={index}
            type="checkbox"
            name={buttonData.areaOfInterest}
            checked={selectedBodyParts.has(buttonData.areaOfInterest)}
            readOnly
          />
        </button>
      ))}
    </>
  );

  return (
    <div style={{ overflowX: "auto", margin: "auto" }}>
      <div
        className="areas-of-interest"
        style={{ paddingLeft: 60, paddingRight: 60, margin: "auto" }}
      >
        <div className="areas-of-interest__front-images">
          <img
            className="areas-of-interest__front-image"
            src={FrontImage}
            alt="front view"
            draggable={false}
          />

          {renderImagesAndButtons(frontImageData, frontButtonData)}
        </div>
        <div className="areas-of-interest__back-images">
          <img
            className="areas-of-interest__front-image"
            src={BackImage}
            alt="back view"
            draggable={false}
          />
          {renderImagesAndButtons(
            backImageData,
            backButtonData,
            "areas-of-interest__back-image",
          )}
        </div>
      </div>
      {Array.from(selectedBodyParts).length === 0 ? (
        <NormalText textContent={" "} />
      ) : Array.from(selectedBodyParts).filter((bodyPart) =>
          isAISupportedArea(bodyPart),
        ).length !== 0 ? (
        <NormalText
          textContent={"AI is available for this body part"}
          style={{
            color: theme?.themeColor,
            textAlign: "center",
          }}
        />
      ) : (
        <NormalText
          textContent={"AI is not available for this body part"}
          style={{ color: "#ff0000", textAlign: "center" }}
        />
      )}
    </div>
  );
};

export default SelectAreasOfInterest;
